import Logger from 'customer-flows/utils/logger'
import { ensureSafeComponent } from '@embroider/util'
import uid from 'customer-flows/utils/uid'
import { steps } from 'customer-flows/components/_config/steps'
import { type StepEntry } from 'customer-flows/components/_config/registry'
import type FlowPage from 'customer-flows/flow/page'
import { FLOW_EXTENSION_PAGE_TYPENAMES } from '@peekops/peek-extensions-framework/classes/FlowExtension'
import { tracked } from '@glimmer/tracking'

export default class Step {
  static components = import.meta.glob('../components/steps/**/*.gts')

  id: string
  type: StepType
  routeName: string
  pageType: PageType
  component?: any
  pageIndex: number
  renderGroup?: number
  alwaysVisible?: boolean
  extensionContext?: Record<string, any>
  settings: Prettify<Omit<StepConfig, keyof BaseStep>> = {}
  mounted: boolean = false
  imposesQuoteRequirement?: PrefixedStoreField
  unsupported: boolean = false

  @tracked element: HTMLElement | null = null

  static async create(flowPage: FlowPage, config: StepConfig, index: number) {
    const { routeName, type } = flowPage
    const { visible, renderGroup, variant, alwaysVisible, ...settings } = config
    const step = new Step()

    step.pageIndex = index
    step.routeName = routeName
    step.pageType = type
    step.type = config.__typename
    step.renderGroup = renderGroup
    step.alwaysVisible = alwaysVisible
    step.id = uid()
    step.settings = { ...settings }

    const extensionRef =
      (config as any).extensionRef ??
      (flowPage.type === FLOW_EXTENSION_PAGE_TYPENAMES.FLOW_BOOKING_EXTENSION_PAGE_TYPENAME
        ? (flowPage as unknown as BookingExtensionPage).settings.extensionRef
        : undefined)

    if (extensionRef) {
      step.settings.extensionRef = extensionRef
    }

    await step._loadComponent(config)

    return step
  }

  async _loadComponent(step: StepConfig) {
    const variant = step.variant ?? 'DEFAULT'
    const foundStep: StepEntry | undefined = steps.find(
      (stepEntry) => stepEntry.type === step.__typename && stepEntry.variant === variant
    )
    const componentPath = foundStep?.componentPath

    if (!foundStep) {
      Logger.error(`No step found for ${step.__typename} with variant ${variant}.`)
      this.unsupported = true
      return
    }

    if ('supportedPages' in foundStep && !foundStep.supportedPages?.includes(this.pageType as never)) {
      Logger.error(`Step '${step.__typename}' is not supported on page '${this.pageType}'.`)
      this.unsupported = true
      return
    }

    if ('imposesQuoteRequirement' in foundStep && foundStep.imposesQuoteRequirement) {
      this.imposesQuoteRequirement = foundStep.imposesQuoteRequirement
    }

    let component = step?.component
    if (!componentPath && !component) return

    try {
      if (component) {
        this.component = ensureSafeComponent(component, step)
        this.extensionContext = {
          accessToken: this.settings.extensionRef?.accessToken,
          // FlowExtension doesn't expose a `.host` getter — the value only
          // lives at `.extensionData.config.host` (set in extendable.ts).
          host: this.settings.extensionRef?.extensionData?.config?.host
        }

        return
      }

      const componentModule = Step.components[`../components/steps/${componentPath}.gts`]

      if (!componentModule) {
        throw new Error(`Component module not found for ${componentPath}.`)
      }

      component = await componentModule() as { default: any }

      const imported = component?.default
      this.component = ensureSafeComponent(imported, step)
    } catch (e) {
      Logger.error(`Error importing component ${componentPath}. ${(e as Error).message}`)
      this.component = class { }
      throw e
    }
  }

  registerElement(element: HTMLElement) {
    this.element = element
  }

  unregisterElement() {
    this.element = null
  }
}

declare global {
  export interface FlowStep extends Step { }
}
